import React, {memo, useEffect, useState} from 'react';
import {useTheme} from "../../../common/ProjectThemeProvider";
import {useToast} from "../../../common/fusion-ui/provider/ToastProvider";
import {
    checkNull,
    checkNullStr,
    downloadExcelFile,
    getJsonValue,
    getJsonValueFromNestedKeys,
    isBoolTrue,
    isJsonValueTrue,
    setTabTitle
} from "../../../common/fusion-ui/utils/ReactUtils";
import {printError, printLog} from "../../../common/CommonUtils";
import {AdminModules, AdminReqUrls} from "../../config/AdminConfig";
import {AdminInterface} from "../../components";
import {GridBox, Section} from "../../../common/fusion-ui";
import ThemedInput from "../../../common/components/themed/ThemedInput";
import ThemedButton from "../../../common/components/themed/ThemedButton";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";
import {AnimationType, FramerAnimator} from "../../../common/fusion-ui/animation/FramerAnimator";
import {CssVariant} from "../../../common/fusion-ui/config/FusionConfig";
import {sendAdminRequest} from "../../utils/AdminUtils";

const ReportsPage = memo(() => {
    const TAG = "ReportsPage";

    const moduleName = AdminModules.Reports;

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isScreenUpSm, isScreenUpMd} = useScreenSize();

    const animator = new FramerAnimator();

    const Fields = {
        usrRpt_startDate: 'usrRpt_startDate',
        usrRpt_endDate: 'usrRpt_endDate',
        usrRptSubmitBtn_asLoading: 'usrRptSubmitBtn_asLoading',
        usrRptSubmitBtn_disable: 'usrRptSubmitBtn_disable',

        usgRpt_startDate: 'usgRpt_startDate',
        usgRpt_endDate: 'usgRpt_endDate',
        usgRptSubmitBtn_asLoading: 'usgRptSubmitBtn_asLoading',
        usgRptSubmitBtn_disable: 'usgRptSubmitBtn_disable',

        mstrRptSubmitBtn_asLoading: 'mstrRptSubmitBtn_asLoading',
        mstrRptSubmitBtn_disable: 'mstrRptSubmitBtn_disable',
    };
    const [fieldsData, setFieldsData] = useState({});


    useEffect(() => {
        setTabTitle(moduleName);
    }, []);

    return (<>
        <AdminInterface moduleName={moduleName} tag={TAG}>
            {animator.setAnimationType(AnimationType.SLIDE_UP).setDelay(200).renderChildren(<div>
                {/*user-report*/}
                {renderExportSection({
                    title: 'User Report',
                    startField: Fields.usrRpt_startDate,
                    endField: Fields.usrRpt_endDate,
                    submitLoadingField: Fields.usrRptSubmitBtn_asLoading,
                    submitDisableField: Fields.usrRptSubmitBtn_disable,
                    exportCallback: handleExportData_usrReport,
                })}

                {/*usage-report*/}
                {renderExportSection({
                    className: 'mt-4',
                    title: 'Usage Report',
                    startField: Fields.usgRpt_startDate,
                    endField: Fields.usgRpt_endDate,
                    submitLoadingField: Fields.usgRptSubmitBtn_asLoading,
                    submitDisableField: Fields.usgRptSubmitBtn_disable,
                    exportCallback: handleExportData_usgReport,
                })}

                {/*master-report*/}
                {renderExportSection({
                    className: 'mt-4',
                    title: 'Master Report',
                    submitLoadingField: Fields.mstrRptSubmitBtn_asLoading,
                    submitDisableField: Fields.mstrRptSubmitBtn_disable,
                    showStart: false,
                    showEnd: false,
                    exportCallback: handleExportData_mstrReport,
                })}
            </div>)}
        </AdminInterface>
    </>)

    function renderExportSection({
                                     className = '',
                                     title = '',
                                     startField,
                                     endField,
                                     submitLoadingField,
                                     submitDisableField,
                                     exportCallback,
                                     showStart = true,
                                     showEnd = true,
                                 }) {
        return <Section className={`p-3 app-section hover ${className}`}
                        titleClassName={'fs-lg px-3'}
                        borderColor={theme.text30}
                        title={title}
                        titleColor={theme.text}
                        titleAt={'start'}
                        roundedVariant={4}
        >
            <GridBox className={'px-2 jc-center-sm'} justifyAt={'start'} md={4} sm={[4, 4, 12]}>
                {/*start-date*/}
                {showStart && <ThemedInput
                    wrapperClassName={'mx-2'}
                    labelClassName={'fw-bold'}
                    className={''}
                    variant={false}
                    showMsg={false}
                    value={getField(startField)}
                    label={"From"}
                    placeholder={"From"}
                    type={'date'}
                    onChange={(e) => updateField(startField, e.target.value)}
                />}
                {/*end-date*/}
                {showEnd && <ThemedInput
                    wrapperClassName={'mx-2'}
                    labelClassName={'fw-bold'}
                    className={''}
                    variant={false}
                    showMsg={false}
                    value={getField(endField)}
                    label={"To"}
                    placeholder={"To"}
                    type={'date'}
                    onChange={(e) => updateField(endField, e.target.value)}
                />}

                {/*export*/}
                <div className={''} style={{marginTop: (isScreenUpMd || (!showStart && !showEnd)) ? '' : '1.36rem'}}>
                    <ThemedButton
                        className={'px-3 py-1 text-nowrap w-auto fs-sm-md'}
                        text={isFieldTrue(submitLoadingField) ? 'Exporting...' : 'Export Excel Report'}
                        disableVariant={'xs'}
                        elasticActive={true}
                        mx={2}
                        variant={theme.primaryBlue}
                        asLoading={isFieldTrue(submitLoadingField)}
                        disabled={isFieldTrue(submitDisableField)}
                        onClick={exportCallback}
                    />
                </div>
            </GridBox>
        </Section>
    }

    function handleExportData_usrReport() {
        handleExportData({
            startField: Fields.usrRpt_startDate,
            endField: Fields.usrRpt_endDate,
            submitLoadingField: Fields.usrRptSubmitBtn_asLoading,
            submitDisableField: Fields.usrRptSubmitBtn_disable,
            reqUrl: AdminReqUrls.admin__expReport__usrRpt_fetchRecords
        });
    }


    function handleExportData_usgReport() {
        handleExportData({
            startField: Fields.usgRpt_startDate,
            endField: Fields.usgRpt_endDate,
            submitLoadingField: Fields.usgRptSubmitBtn_asLoading,
            submitDisableField: Fields.usgRptSubmitBtn_disable,
            reqUrl: AdminReqUrls.admin__expReport__usagesRpt_fetchRecords
        });
    }

    function handleExportData_mstrReport() {
        handleExportData({
            submitLoadingField: Fields.mstrRptSubmitBtn_asLoading,
            submitDisableField: Fields.mstrRptSubmitBtn_disable,
            reqUrl: AdminReqUrls.admin__expReport__masterRpt_fetchRecords
        });
    }

    function handleExportData({
                                  startField,
                                  endField,
                                  submitLoadingField,
                                  submitDisableField,
                                  reqUrl,
                              }) {
        const fun = 'handleExportData:';
        const proceed = true;

        const startDate = getField(startField);
        const endDate = getField(endField);

        if (checkNull(startField) && !checkNullStr(startDate)) {
            toast("Start-date cannot be empty!", CssVariant.danger);
            return false;
        } else if (checkNull(endField) && !checkNullStr(endDate)) {
            toast("End-date cannot be empty!", CssVariant.danger);
            return false;
        }

        if (proceed) {
            updateField(submitLoadingField, true);
            updateField(submitDisableField, true);
            const REQUEST_OPTIONS = {
                startDate, endDate
            };
            try {
                sendAdminRequest({
                    reqUrl: reqUrl,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        const message = getJsonValue(fetchedData, 'message');
                        const success = isJsonValueTrue(fetchedData, 'success');

                        if (success) {
                            // toast(message, CssVariant.success);
                            const fileName = getJsonValueFromNestedKeys(fetchedData, ['data', 'fileName']);
                            const columns = getJsonValueFromNestedKeys(fetchedData, ['data', 'columns']);
                            const rows = getJsonValueFromNestedKeys(fetchedData, ['data', 'rows']);
                            log(fun, 'fetchedData.columns:', columns, 'rows:', rows);

                            //Download excel-file
                            downloadExcelFile({fileName: fileName, columns: columns, rows: rows});
                            toast("Report exported!", CssVariant.info);
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        updateField(submitDisableField, false);
                        updateField(submitLoadingField, false);
                    }),
                    onError: ((error) => {
                        toast("Export failed, please try again!", CssVariant.danger);
                        updateField(submitLoadingField, false);
                        updateField(submitDisableField, false);
                        logErr(fun, error);
                    }),
                });
            } catch (error) {
                toast("Export failed, please try again!", CssVariant.danger);
                updateField(submitLoadingField, false);
                updateField(submitDisableField, false);
                logErr(fun, error);
            }
        } else {
            toast('Action disabled!', CssVariant.warning);
        }
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text + '');
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default ReportsPage;
